/**
 * Settings Page - Hacker Profile
 * ===============================
 * Customize your CodeForge experience!
 * Update profile, change password, view achievements.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Container, 
  User,
  Settings,
  Shield,
  Bell,
  Palette,
  Trophy,
  Activity,
  Brain,
  LogOut,
  Flame,
  Terminal,
  Save,
  Loader2,
  Medal,
  Star,
  Zap,
  Target,
  Code2,
  Rocket,
  Crown
} from "lucide-react";
import { toast } from "sonner";

// Sidebar Component
const Sidebar = ({ activePage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { id: "dashboard", label: "MISSION CONTROL", icon: <Activity className="w-5 h-5" />, path: "/dashboard" },
    { id: "sites", label: "DEPLOYMENTS", icon: <Container className="w-5 h-5" />, path: "/sites" },
    { id: "ml-studio", label: "ROBOT GYM", icon: <Brain className="w-5 h-5" />, path: "/ml-studio" },
    { id: "tutorials", label: "FRICK & FRACK GUIDES", icon: <Star className="w-5 h-5" />, path: "/tutorials" },
    { id: "settings", label: "SETTINGS", icon: <Settings className="w-5 h-5" />, path: "/settings" },
  ];

  return (
    <div className="w-64 bg-[#16191F] border-r border-white/5 flex flex-col min-h-screen">
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Terminal className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CODEFORGE
          </span>
        </div>
      </div>

      <div className="p-4 m-4 bg-black/30 border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono text-muted-foreground uppercase">LEVEL {user?.level || 1}</span>
          <span className="text-xs font-mono text-primary">{user?.xp || 0} XP</span>
        </div>
        <Progress value={(user?.xp || 0) % 100} className="h-2 bg-muted" />
        <div className="flex items-center gap-2 mt-3">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-xs font-mono text-white">{user?.streak || 0} day streak</span>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-mono transition-colors ${
                activePage === item.id
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold text-sm">{user?.username?.[0]?.toUpperCase()}</span>
            </div>
            <span className="text-sm font-mono text-white">{user?.username}</span>
          </div>
          <button onClick={logout} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Achievement Badge Component
const AchievementBadge = ({ achievement, unlocked }) => {
  return (
    <div className={`p-4 border ${unlocked ? 'border-primary/30 bg-primary/5' : 'border-white/5 bg-black/20 opacity-50'}`}>
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${unlocked ? 'bg-primary/20' : 'bg-white/5'}`}>
          {achievement.icon}
        </div>
        <div>
          <h4 className={`font-bold ${unlocked ? 'text-white' : 'text-muted-foreground'}`}>
            {achievement.name}
          </h4>
          <p className="text-xs text-muted-foreground font-mono">
            {achievement.description}
          </p>
        </div>
      </div>
      {unlocked && (
        <div className="mt-2 text-xs text-primary font-mono">
          ✓ Unlocked
        </div>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    notifications: true,
    darkMode: true,
    soundEffects: true,
    showXPAnimations: true,
  });

  const achievements = [
    { id: 'first_site', name: 'First Deploy', description: 'Deploy your first site', icon: <Rocket className="w-6 h-6 text-blue-400" />, unlocked: true },
    { id: 'level_5', name: 'Rising Star', description: 'Reach Level 5', icon: <Star className="w-6 h-6 text-yellow-400" />, unlocked: (user?.level || 1) >= 5 },
    { id: 'level_10', name: 'Pro Hacker', description: 'Reach Level 10', icon: <Crown className="w-6 h-6 text-purple-400" />, unlocked: (user?.level || 1) >= 10 },
    { id: 'streak_7', name: 'Week Warrior', description: '7 day streak', icon: <Flame className="w-6 h-6 text-orange-400" />, unlocked: (user?.streak || 0) >= 7 },
    { id: 'ml_master', name: 'Robot Trainer', description: 'Train 5 ML models', icon: <Brain className="w-6 h-6 text-green-400" />, unlocked: false },
    { id: 'code_ninja', name: 'Code Ninja', description: 'Save 100 files', icon: <Code2 className="w-6 h-6 text-cyan-400" />, unlocked: false },
  ];

  const handleSaveSettings = async () => {
    setSaving(true);
    // Simulate saving
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success("Settings saved!");
    setSaving(false);
  };

  return (
    <div className="flex min-h-screen bg-[#0B0D10]">
      <Sidebar activePage="settings" />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="border-b border-white/5 bg-[#16191F]/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="px-8 py-4">
            <h1 
              className="text-2xl font-bold text-white uppercase tracking-tight flex items-center gap-3"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="settings-title"
            >
              <Settings className="w-7 h-7 text-primary" />
              HACKER PROFILE
            </h1>
            <p className="text-xs font-mono text-muted-foreground mt-1">
              Customize your CodeForge experience
            </p>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-8">
          {/* Profile Card */}
          <div className="bg-[#16191F] border border-white/5 p-6 mb-8">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center">
                <span className="text-primary font-black text-3xl">
                  {user?.username?.[0]?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {user?.username}
                </h2>
                <p className="text-muted-foreground font-mono text-sm">{user?.email}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1">
                    <Zap className="w-4 h-4 text-primary" />
                    <span className="text-white font-mono text-sm">{user?.xp || 0} XP</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Trophy className="w-4 h-4 text-yellow-400" />
                    <span className="text-white font-mono text-sm">Level {user?.level || 1}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Flame className="w-4 h-4 text-orange-400" />
                    <span className="text-white font-mono text-sm">{user?.streak || 0} day streak</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Tabs defaultValue="preferences" className="w-full">
            <TabsList className="bg-[#16191F] border border-white/5 p-1">
              <TabsTrigger value="preferences" className="font-mono text-sm data-[state=active]:bg-primary data-[state=active]:text-black">
                PREFERENCES
              </TabsTrigger>
              <TabsTrigger value="achievements" className="font-mono text-sm data-[state=active]:bg-primary data-[state=active]:text-black">
                ACHIEVEMENTS
              </TabsTrigger>
              <TabsTrigger value="stats" className="font-mono text-sm data-[state=active]:bg-primary data-[state=active]:text-black">
                STATS
              </TabsTrigger>
            </TabsList>

            {/* Preferences Tab */}
            <TabsContent value="preferences" className="mt-6">
              <div className="bg-[#16191F] border border-white/5 p-6 space-y-6">
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  PREFERENCES
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-black/30 border border-white/5">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-white font-mono">Notifications</p>
                        <p className="text-xs text-muted-foreground">Receive XP and level up alerts</p>
                      </div>
                    </div>
                    <Switch 
                      checked={settings.notifications}
                      onCheckedChange={(checked) => setSettings({...settings, notifications: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-black/30 border border-white/5">
                    <div className="flex items-center gap-3">
                      <Palette className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-white font-mono">Dark Mode</p>
                        <p className="text-xs text-muted-foreground">Keep the lights off (recommended)</p>
                      </div>
                    </div>
                    <Switch 
                      checked={settings.darkMode}
                      onCheckedChange={(checked) => setSettings({...settings, darkMode: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-black/30 border border-white/5">
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-white font-mono">XP Animations</p>
                        <p className="text-xs text-muted-foreground">Show XP flying animations</p>
                      </div>
                    </div>
                    <Switch 
                      checked={settings.showXPAnimations}
                      onCheckedChange={(checked) => setSettings({...settings, showXPAnimations: checked})}
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-primary text-black font-bold"
                  data-testid="save-settings-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  SAVE SETTINGS
                </Button>
              </div>
            </TabsContent>

            {/* Achievements Tab */}
            <TabsContent value="achievements" className="mt-6">
              <div className="bg-[#16191F] border border-white/5 p-6">
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  ACHIEVEMENTS
                </h3>
                <p className="text-muted-foreground font-mono text-sm mb-6">
                  {achievements.filter(a => a.unlocked).length} / {achievements.length} unlocked
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {achievements.map((achievement) => (
                    <AchievementBadge 
                      key={achievement.id} 
                      achievement={achievement} 
                      unlocked={achievement.unlocked}
                    />
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Stats Tab */}
            <TabsContent value="stats" className="mt-6">
              <div className="bg-[#16191F] border border-white/5 p-6">
                <h3 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  YOUR STATS
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-black/30 border border-white/5 text-center">
                    <div className="text-3xl font-black text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {user?.xp || 0}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-1">TOTAL XP</div>
                  </div>
                  <div className="p-4 bg-black/30 border border-white/5 text-center">
                    <div className="text-3xl font-black text-yellow-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {user?.level || 1}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-1">LEVEL</div>
                  </div>
                  <div className="p-4 bg-black/30 border border-white/5 text-center">
                    <div className="text-3xl font-black text-orange-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {user?.streak || 0}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-1">DAY STREAK</div>
                  </div>
                  <div className="p-4 bg-black/30 border border-white/5 text-center">
                    <div className="text-3xl font-black text-green-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {achievements.filter(a => a.unlocked).length}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-1">BADGES</div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
